import { Injectable } from '@angular/core';
import { AkitaAuthQuery } from '@app/akita/api/auth/state/auth.query';
import { TranslocoService } from '@ngneat/transloco';
import { formatApplePayPrice } from '../utils/stripe.utils';
import {
  Stripe,
  PaymentRequestOptions,
  PaymentRequest,
  PaymentRequestShippingOption,
  PaymentRequestItem,
} from '@stripe/stripe-js';
import { PriceModel } from '@app/shared/models/api/price.model';
import { Product } from '@app/akita/api/products/models/product.model';
import { Insurance } from '@app/akita/api/products/models/insurance.model';

@Injectable({ providedIn: 'root' })
export class StripePaymentRequestService {
  constructor(
    private readonly akitaAuthQuery: AkitaAuthQuery,
    private readonly translateService: TranslocoService
  ) {}

  public generatePaymentRequestOptions(
    products?: Array<Product> | null,
    insurances?: Array<Insurance> | null,
    pendingPrice?: boolean | null,
    deliveryProvider?: string | null,
    deliveryFee?: number | null,
    popsyFee?: number | null,
    skipPayerEmail?: boolean | null,
    discountAmount?: PriceModel | null,
    protectionPrice?: PriceModel | null
  ): PaymentRequestOptions {
    let requestEmail = !skipPayerEmail;
    if (this.akitaAuthQuery.isLoggedIn) {
      requestEmail = false;
    }

    const country = 'AE'; // Default country, can be changed based on user settings or location

    const calculatedPrices = this.calculatePrices(
      products,
      insurances,
      deliveryProvider,
      deliveryFee,
      popsyFee,
      Boolean(pendingPrice),
      discountAmount,
      protectionPrice
    );

    return {
      country: country,
      requestPayerName: !this.akitaAuthQuery.isLoggedIn,
      requestPayerEmail: requestEmail,
      requestPayerPhone: !this.akitaAuthQuery.isLoggedIn,
      requestShipping: !this.akitaAuthQuery.isLoggedIn,
      currency: `${products?.[0]?.price.currency || ''}`.toLowerCase(),
      ...calculatedPrices,
    };
  }

  private calculatePrices(
    products?: Array<Product> | null,
    insurances?: Array<Insurance> | null,
    deliveryProvider?: string | null,
    delivery?: number | null,
    popsyFee?: number | null,
    pending?: boolean | null,
    discountAmount?: PriceModel | null,
    protectionPrice?: PriceModel | null
  ): {
    total: PaymentRequestItem;
    displayItems: PaymentRequestItem[] | undefined;
    shippingOptions: PaymentRequestShippingOption[] | undefined;
  } {
    const displayItems = new Array(0);
    const shippingOptions = new Array(0);
    const currency = products?.[0]?.price?.currency?.toUpperCase() || 'SAR';

    let total =
      products?.reduce(
        (tot, current) => tot + current?.price.amount * (current?.quantity || 1),
        0
      ) || 0;

    if (insurances && insurances.length > 0) {
      total = total + insurances.reduce((tot, current) => tot + current.price.amount, 0);
    }

    products?.forEach((x) => {
      displayItems.push({
        amount: formatApplePayPrice(x.price.amount, currency),
        label: x.title || '',
      });
    });

    if (protectionPrice) {
      total = total + protectionPrice.amount;
      displayItems.push({
        amount: formatApplePayPrice(protectionPrice.amount || 0, currency),
        label: this.translateService.translate('txt_popsy_protect'),
      });
    }

    if (discountAmount?.amount) {
      total = total - discountAmount?.amount;

      displayItems.push({
        amount: formatApplePayPrice(discountAmount.amount || 0, currency),
        label: this.translateService.translate('label_discount_code'),
      });
    }

    if (delivery || delivery === 0) {
      total = total + delivery;

      displayItems.push({
        amount: formatApplePayPrice(delivery || 0, currency),
        label: this.translateService.translate('deliver_with_popsy'),
      });

      shippingOptions.push({
        identifier: `${deliveryProvider}`,
        amount: formatApplePayPrice(delivery || 0, currency),
        label: this.translateService.translate('label_delivery_service'),
        detail: this.translateService.translate('deliver_with_popsy'),
      });
    }

    // If Popsy Fee is 0, don't show
    if (popsyFee) {
      total = total + popsyFee;

      displayItems.push({
        amount: formatApplePayPrice(popsyFee || 0, currency),
        label: this.translateService.translate('txt_popsy_protect'),
      });
    }

    return {
      shippingOptions: shippingOptions,
      displayItems: displayItems,
      total: {
        amount: formatApplePayPrice(total, currency),
        label: this.translateService.translate('label_estimated_price_total'),
        pending: Boolean(pending), // Indicate this price is final
      },
    };
  }

  public generatePaymentRequest(
    stripeInstance?: Stripe | null,
    paymentRequestOptions?: PaymentRequestOptions | null
  ): PaymentRequest | null {
    if (stripeInstance && paymentRequestOptions) {
      paymentRequestOptions.country = 'AE'; // Default country, can be changed based on user settings or location

      return stripeInstance.paymentRequest(paymentRequestOptions);
    } else {
      return null;
    }
  }
}
